import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Screen Wake Lock, so the display does not sleep mid-table.
 *
 * A NAUI phase 8 O2 table runs about 45 minutes with long stretches of no
 * interaction. Without a wake lock the screen sleeps, and on some platforms that
 * also throttles timers and suspends audio — losing the cues the user is relying
 * on with their eyes shut.
 *
 * Support is uneven (no Safari on iOS at the time of writing), so this is
 * strictly best-effort: `supported` is exposed so the UI can suggest raising the
 * OS screen timeout instead, and everything no-ops harmlessly otherwise.
 */

interface WakeLockSentinel {
  released: boolean;
  release: () => Promise<void>;
  addEventListener: (type: string, listener: () => void) => void;
}

interface WakeLockNavigator {
  wakeLock?: { request: (type: 'screen') => Promise<WakeLockSentinel> };
}

export const useWakeLock = () => {
  const sentinel = useRef<WakeLockSentinel | null>(null);
  const [active, setActive] = useState(false);
  const wantLock = useRef(false);

  const supported = typeof navigator !== 'undefined'
    && 'wakeLock' in (navigator as unknown as WakeLockNavigator);

  const request = useCallback(async (): Promise<boolean> => {
    wantLock.current = true;
    const nav = navigator as unknown as WakeLockNavigator;
    if (!nav.wakeLock) return false;

    try {
      const lock = await nav.wakeLock.request('screen');
      sentinel.current = lock;
      setActive(true);
      lock.addEventListener('release', () => {
        sentinel.current = null;
        setActive(false);
      });
      return true;
    } catch {
      // Denied, or not permitted in this context. Session continues regardless.
      setActive(false);
      return false;
    }
  }, []);

  const release = useCallback(async (): Promise<void> => {
    wantLock.current = false;
    try {
      await sentinel.current?.release();
    } catch {
      // Already gone; nothing to do.
    }
    sentinel.current = null;
    setActive(false);
  }, []);

  // The browser drops the lock whenever the tab is hidden, and does not restore
  // it on return. Re-acquire so a mid-session tab switch does not silently leave
  // the screen free to sleep for the rest of the table.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible' && wantLock.current && !sentinel.current) {
        void request();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [request]);

  useEffect(() => () => {
    wantLock.current = false;
    void sentinel.current?.release().catch(() => undefined);
  }, []);

  return { supported, active, request, release };
};
